import {
  CheckCircle2,
  Clock,
  CircleDollarSign,
  Eye,
  Pencil,
  Plus,
  ReceiptText,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom'

import DetalleGastoModal from '../components/gastos/DetalleGastoModal'
import ModalMensaje from '../components/ui/ModalMensaje'
import { useAuth } from '../context/AuthContext'
import { useEmpresa } from '../context/EmpresaContext'
import { useModalMensaje } from '../hooks/useModalMensaje'
import { supabase } from '../lib/supabase'
import { cargarFacturasPorGasto } from '../services/abastecimiento'
import {
  formatearFechaGasto,
  formatearMontoGasto,
  formatearTipoDocumento,
  obtenerEstadoPago,
  TIPOS_DOCUMENTO,
} from '../utils/gastos'
import {
  puedeCrearGastos,
  mensajeErrorGasto,
  puedeEliminarGasto,
  puedeGestionarGasto,
} from '../utils/permisosGastos'

function TarjetaResumen({ icono: Icono, etiqueta, valor }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <Icono className="text-emerald-600" />

      <p className="mt-5 text-sm text-slate-500">{etiqueta}</p>

      <p className="mt-1 text-2xl font-bold text-slate-900">
        {valor}
      </p>
    </div>
  )
}

function Gastos() {
  const { user } = useAuth()
  const { empresaActual } = useEmpresa()
  const location = useLocation()
  const navigate = useNavigate()
  const { modal, abrirModal, actualizarModal } = useModalMensaje()

  const [gastos, setGastos] = useState([])
  const [facturasPorGasto, setFacturasPorGasto] = useState(new Map())
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')

  const [busqueda, setBusqueda] = useState('')
  const [filtroEstadoPago, setFiltroEstadoPago] = useState('')
  const [filtroTipoDocumento, setFiltroTipoDocumento] =
    useState('')

  const [gastoDetalle, setGastoDetalle] = useState(null)
  const [detalleAbierto, setDetalleAbierto] = useState(false)

  const rol = empresaActual?.rol
  const puedeCrear = puedeCrearGastos(rol)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!empresaActual?.id) {
      setGastos([])
      setCargando(false)
      return
    }

    cargarGastos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  useEffect(() => {
    if (!location.state) return

    if (location.state.recargar) {
      cargarGastos()
    }

    navigate('/gastos', { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  // Acceso de solo lectura desde Abastecimiento: /gastos?ver=<id>
  const idPorVer = new URLSearchParams(location.search).get('ver')

  useEffect(() => {
    if (!idPorVer || cargando) return

    const gastoPorVer = gastos.find((gasto) => gasto.id === idPorVer)

    if (gastoPorVer) {
      setGastoDetalle(gastoPorVer)
      setDetalleAbierto(true)
    }

    navigate('/gastos', { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPorVer, cargando, gastos])

  const cargarGastos = async () => {
    if (!empresaActual?.id) return

    try {
      setCargando(true)
      setError('')

      const { data, error: errorConsulta } = await supabase
        .from('gastos')
        .select(`
          id,
          empresa_id,
          categoria_id,
          proveedor_id,
          fecha_gasto,
          fecha_vencimiento,
          fecha_pago,
          tipo_documento,
          numero_documento,
          descripcion,
          monto_neto,
          monto_exento,
          monto_iva,
          otros_impuestos,
          monto_total,
          medio_pago,
          estado_pago,
          estado_registro,
          observaciones,
          creado_por,
          creado_en,
          actualizado_en,
          categoria:categorias_gasto!fk_gasto_categoria_empresa (
            id,
            nombre
          ),
          proveedor:proveedores!fk_gasto_proveedor_empresa (
            id,
            razon_social,
            rut
          )
        `)
        .eq('empresa_id', empresaActual.id)
        .neq('estado_registro', 'anulado')
        .order('fecha_gasto', { ascending: false })
        .order('creado_en', { ascending: false })

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      const lista = data || []
      const facturas = await cargarFacturasPorGasto(
        empresaActual.id,
        lista.map((gasto) => gasto.id)
      )

      if (!montadoRef.current) return

      setFacturasPorGasto(
        new Map(facturas.map((factura) => [factura.gasto_id, factura]))
      )
      setGastos(lista)
    } catch (errorCarga) {
      console.error('Error al cargar gastos:', errorCarga)

      if (!montadoRef.current) return

      setError(
        errorCarga.message || 'No fue posible cargar los gastos.'
      )
      setGastos([])
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const confirmarEliminacion = async (gasto) => {
    actualizarModal({
      procesando: true,
      mostrarCancelar: false,
      textoAceptar: 'Eliminando...',
    })

    try {
      const { data: gastoAnulado, error: errorAnular } =
        await supabase
          .from('gastos')
          .update({ estado_registro: 'anulado' })
          .eq('id', gasto.id)
          .eq('empresa_id', empresaActual.id)
          .select('id')
          .maybeSingle()

      if (errorAnular) {
        throw errorAnular
      }

      if (!gastoAnulado) {
        throw new Error(
          'No tienes permisos para eliminar este gasto.'
        )
      }

      if (!montadoRef.current) return

      setGastos((actuales) =>
        actuales.filter((registro) => registro.id !== gasto.id)
      )

      if (gastoDetalle?.id === gasto.id) {
        setDetalleAbierto(false)
        setGastoDetalle(null)
      }

      abrirModal({
        tipo: 'success',
        titulo: 'Gasto eliminado',
        mensaje: 'El gasto fue eliminado correctamente.',
      })
    } catch (errorEliminar) {
      console.error('Error al eliminar gasto:', errorEliminar)

      if (!montadoRef.current) return

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo eliminar',
        mensaje: mensajeErrorGasto(errorEliminar),
      })
    }
  }

  const eliminarGasto = (gasto) => {
    abrirModal({
      tipo: 'confirm',
      titulo: 'Eliminar gasto',
      mensaje:
        '¿Estás seguro de que deseas eliminar este gasto? El registro se conservará como anulado.',
      textoAceptar: 'Eliminar',
      textoCancelar: 'Cancelar',
      mostrarCancelar: true,
      onAceptar: () => confirmarEliminacion(gasto),
    })
  }

  const verGasto = (gasto) => {
    setGastoDetalle(gasto)
    setDetalleAbierto(true)
  }

  const cerrarDetalle = () => {
    setDetalleAbierto(false)
    setGastoDetalle(null)
  }

  const editarGasto = (gastoId) => {
    setDetalleAbierto(false)
    navigate(`/gastos/${gastoId}/editar`)
  }

  const gastosFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase()

    return gastos.filter((gasto) => {
      const coincideBusqueda =
        !termino ||
        (gasto.descripcion || '')
          .toLowerCase()
          .includes(termino) ||
        (gasto.numero_documento || '')
          .toLowerCase()
          .includes(termino) ||
        (gasto.proveedor?.razon_social || '')
          .toLowerCase()
          .includes(termino) ||
        (gasto.categoria?.nombre || '')
          .toLowerCase()
          .includes(termino)

      const coincideEstado =
        !filtroEstadoPago ||
        gasto.estado_pago === filtroEstadoPago

      const coincideTipo =
        !filtroTipoDocumento ||
        gasto.tipo_documento === filtroTipoDocumento

      return coincideBusqueda && coincideEstado && coincideTipo
    })
  }, [gastos, busqueda, filtroEstadoPago, filtroTipoDocumento])

  const resumen = useMemo(() => {
    return gastosFiltrados.reduce(
      (acumulado, gasto) => {
        const total = Number(gasto.monto_total) || 0

        acumulado.cantidad += 1
        acumulado.totalGeneral += total

        if (gasto.estado_pago === 'pagado') {
          acumulado.totalPagado += total
        } else {
          acumulado.totalPendiente += total
        }

        return acumulado
      },
      {
        cantidad: 0,
        totalGeneral: 0,
        totalPendiente: 0,
        totalPagado: 0,
      }
    )
  }, [gastosFiltrados])

  const hayFiltrosActivos =
    Boolean(busqueda.trim()) ||
    Boolean(filtroEstadoPago) ||
    Boolean(filtroTipoDocumento)

  const limpiarFiltros = () => {
    setBusqueda('')
    setFiltroEstadoPago('')
    setFiltroTipoDocumento('')
  }

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <RefreshCw
              size={38}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-4 font-medium text-slate-500">
              Cargando gastos...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">{error}</p>

          <button
            type="button"
            onClick={cargarGastos}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      )
    }

    if (gastos.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <ReceiptText
              size={28}
              className="text-emerald-600"
            />
          </div>

          <h3 className="mt-5 text-lg font-bold text-slate-900">
            No hay gastos registrados
          </h3>

          <p className="mt-2 text-slate-500">
            Presiona “Nuevo gasto” para ingresar el primer
            registro.
          </p>

          <Link
            to="/gastos/nuevo"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-600 px-5 py-3 font-semibold text-emerald-700 transition hover:bg-emerald-50"
          >
            <Plus size={19} />
            Ingresar primer gasto
          </Link>
        </div>
      )
    }

    return (
      <>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <div className="relative sm:max-w-xs sm:flex-1">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              placeholder="Buscar por descripción, número, proveedor o categoría"
              className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          <select
            value={filtroEstadoPago}
            onChange={(event) =>
              setFiltroEstadoPago(event.target.value)
            }
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 sm:w-auto"
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="parcial">Pago parcial</option>
            <option value="pagado">Pagado</option>
          </select>

          <select
            value={filtroTipoDocumento}
            onChange={(event) =>
              setFiltroTipoDocumento(event.target.value)
            }
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 sm:w-auto"
          >
            <option value="">Todos los documentos</option>

            {Object.entries(TIPOS_DOCUMENTO).map(
              ([valor, etiqueta]) => (
                <option key={valor} value={valor}>
                  {etiqueta}
                </option>
              )
            )}
          </select>
        </div>

        {gastosFiltrados.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <p className="text-slate-500">
              No se encontraron gastos para los filtros
              aplicados.
            </p>

            {hayFiltrosActivos && (
              <button
                type="button"
                onClick={limpiarFiltros}
                className="mt-4 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm md:min-w-[1024px]">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Fecha</th>
                  <th className="px-6 py-3">Proveedor</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Documento
                  </th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Número
                  </th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Categoría
                  </th>
                  <th className="px-6 py-3">Descripción</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Estado de pago
                  </th>
                  <th className="px-6 py-3 text-right">Total</th>
                  <th className="px-6 py-3 text-right">
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {gastosFiltrados.map((gasto) => {
                  const estado = obtenerEstadoPago(
                    gasto.estado_pago
                  )

                  return (
                    <tr key={gasto.id}>
                      <td className="whitespace-nowrap px-6 py-4 text-slate-600">
                        {formatearFechaGasto(gasto.fecha_gasto)}
                      </td>

                      <td className="px-6 py-4">
                        {gasto.proveedor ? (
                          <div>
                            <p className="font-semibold text-slate-900">
                              {gasto.proveedor.razon_social}
                            </p>

                            {gasto.proveedor.rut && (
                              <p className="text-xs text-slate-500">
                                {gasto.proveedor.rut}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">
                            Sin proveedor
                          </span>
                        )}
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 text-slate-600 md:table-cell">
                        {formatearTipoDocumento(
                          gasto.tipo_documento
                        )}
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 text-slate-600 md:table-cell">
                        {gasto.numero_documento || '—'}
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 text-slate-600 md:table-cell">
                        {gasto.categoria?.nombre || 'Sin categoría'}
                      </td>

                      <td
                        className="max-w-xs truncate px-6 py-4 text-slate-600"
                        title={gasto.descripcion}
                      >
                        {gasto.descripcion}
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 md:table-cell">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estado.clase}`}
                        >
                          {estado.etiqueta}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-right font-semibold text-slate-900">
                        {formatearMontoGasto(gasto.monto_total)}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => verGasto(gasto)}
                            title="Ver"
                            aria-label={`Ver gasto ${gasto.descripcion}`}
                            className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                          >
                            <Eye size={16} />
                          </button>

                          {puedeGestionarGasto(rol, gasto, user?.id) && (
                            <button
                              type="button"
                              onClick={() =>
                                editarGasto(gasto.id)
                              }
                              title="Editar"
                              aria-label={`Editar gasto ${gasto.descripcion}`}
                              className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                            >
                              <Pencil size={16} />
                            </button>
                          )}

                          {puedeEliminarGasto(
                            rol,
                            gasto,
                            user?.id,
                            facturasPorGasto.has(gasto.id)
                          ) && (
                            <button
                              type="button"
                              onClick={() => eliminarGasto(gasto)}
                              title="Eliminar"
                              aria-label={`Eliminar gasto ${gasto.descripcion}`}
                              className="flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Administración
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Gastos
          </h2>

          <p className="mt-2 text-slate-500">
            Gastos registrados para{' '}
            {empresaActual?.nombre_fantasia}.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={cargarGastos}
            disabled={cargando}
            className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              size={18}
              className={cargando ? 'animate-spin' : ''}
            />
            Actualizar
          </button>

          {puedeCrear && (
            <Link
              to="/gastos/nuevo"
              className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700"
            >
              <Plus size={20} />
              Nuevo gasto
            </Link>
          )}
        </div>
      </div>

      {!cargando && !error && gastos.length > 0 && (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <TarjetaResumen
            icono={ReceiptText}
            etiqueta="Cantidad de gastos"
            valor={resumen.cantidad}
          />

          <TarjetaResumen
            icono={CircleDollarSign}
            etiqueta="Total general"
            valor={formatearMontoGasto(resumen.totalGeneral)}
          />

          <TarjetaResumen
            icono={Clock}
            etiqueta="Total pendiente"
            valor={formatearMontoGasto(resumen.totalPendiente)}
          />

          <TarjetaResumen
            icono={CheckCircle2}
            etiqueta="Total pagado"
            valor={formatearMontoGasto(resumen.totalPagado)}
          />
        </div>
      )}

      {renderContenido()}

      <DetalleGastoModal
        abierto={detalleAbierto}
        gasto={gastoDetalle}
        onCerrar={cerrarDetalle}
        facturaVinculada={
          gastoDetalle ? facturasPorGasto.get(gastoDetalle.id) : null
        }
        onEditar={
          puedeGestionarGasto(rol, gastoDetalle, user?.id)
            ? editarGasto
            : undefined
        }
      />

      <ModalMensaje {...modal} />
    </div>
  )
}

export default Gastos
